import { Link } from 'react-router-dom';
import { ArrowRight, GitBranch, BookOpen, Building2, Sparkles, Briefcase, Check } from 'lucide-react';
import SEO from '../components/SEO';
import { PageHero, Reveal, SectionHeading } from '../components/Reveal';
import Services from '../components/home/Services';
import FinalCTA from '../components/home/FinalCTA';

const AREAS = [
  {
    icon: GitBranch,
    title: 'Stream Selection After 10th',
    text: 'Science, Commerce, or Humanities — the choice shapes everything after it. We help you match your interests, strengths, and career goals to the right stream, with clarity on where each stream actually leads.',
    points: ['Interest & strengths-based stream mapping', 'Career outcomes of each stream, explained simply', 'Guidance for students and parents together'],
  },
  {
    icon: BookOpen,
    title: 'Course Selection After 12th',
    text: 'The right course is the one whose outcomes match your goals — not just the most popular one. Compare courses, specialisations, and future scope before you commit years to a decision.',
    points: ['Course comparisons based on career outcomes', 'Specialisation and future-scope clarity', 'Backup options and alternate pathways'],
  },
  {
    icon: Building2,
    title: 'College Selection',
    text: 'Make informed college decisions based on your goals, budget, location, and career outcomes — not just brand names and rankings.',
    points: ['College shortlisting framework', 'Placements, faculty, and ROI comparison', 'Budget and location planning'],
  },
  {
    icon: Sparkles,
    title: 'Skill Development & Upskilling',
    text: 'Degrees open doors; skills get you hired. Identify the exact skills your target career needs and follow a practical learning plan with certifications, tools, and projects.',
    points: ['Skill-gap analysis for your target career', 'Certification and learning-path recommendations', 'Project and portfolio guidance'],
  },
  {
    icon: Briefcase,
    title: 'Placement & Job Readiness',
    text: 'From resume to interview room — get the professional skills and preparation that turn applications into offers.',
    points: ['Resume building and review', 'Interview and communication preparation', 'Internship and placement strategy'],
  },
];

const CareerGuidance = () => (
  <main data-testid="career-guidance-page">
    <SEO
      title="Career Guidance for Students & Professionals | CareerWithRaaj"
      description="Personalised career guidance: stream selection after 10th, course selection after 12th, college selection, skill development, and placement readiness."
    />
    <PageHero
      overline="Career Guidance"
      title="Guidance That Goes Beyond “What to Study”"
      description="We help you understand where each choice can take you — and what you need to do next. From 8th grade to career transitions, get practical, personalised direction."
    >
      <Reveal delay={0.3}>
        <Link
          to="/book-consultation"
          data-testid="guidance-hero-cta"
          className="mt-9 inline-flex items-center gap-2 rounded-full bg-coral px-8 py-4 text-base font-semibold text-white transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-coral-deep"
        >
          Book a Career Consultation <ArrowRight className="h-4 w-4" />
        </Link>
      </Reveal>
    </PageHero>

    <section className="bg-white py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <SectionHeading
          index="01"
          overline="What We Cover"
          title="Every Career Decision, Covered"
          description="Five areas where the right guidance changes everything."
        />
        <div className="mt-16 space-y-6">
          {AREAS.map((area, i) => {
            const Icon = area.icon;
            return (
              <Reveal key={area.title} delay={0.04 * i}>
                <div
                  className="grid gap-6 rounded-3xl border border-line bg-white p-8 shadow-card transition-[box-shadow,transform] duration-300 hover:-translate-y-1 hover:shadow-card-hover md:grid-cols-12 md:p-10"
                  data-testid={`guidance-area-${area.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                >
                  <div className="md:col-span-5">
                    <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-coral/10 text-coral">
                      <Icon className="h-5 w-5" />
                    </span>
                    <h3 className="mt-5 font-display text-xl font-semibold text-navy md:text-2xl">{area.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-slate-600 md:text-base">{area.text}</p>
                  </div>
                  <ul className="space-y-3 self-center md:col-span-7 md:border-l md:border-line md:pl-10">
                    {area.points.map((p) => (
                      <li key={p} className="flex items-start gap-3 text-sm text-slate-700 md:text-base">
                        <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-coral/10">
                          <Check className="h-3 w-3 text-coral" />
                        </span>
                        {p}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>

    <Services />
    <FinalCTA />
  </main>
);

export default CareerGuidance;
